export interface CoalescedTrigger {
  /** Ask for a run. Free to call on every event. */
  request(): void;
  /** Stop for good: drops a queued run and ignores everything after. */
  cancel(): void;
}

export interface CoalescedTriggerOptions {
  /** Minimum wall-clock gap between two runs. */
  intervalMs: number;
  run: () => void;
  /** Injected in tests; defaults to the real clock and the real timer. */
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => number;
  clearTimer?: (handle: number) => void;
}

/**
 * Rate-limit a side effect to at most one run per interval, leading edge first
 * and never dropping the last request.
 *
 * The encrypted-session bus emits about one event per second per live session,
 * and several sessions can fly at once; every one of them wants the same single
 * `recentEncrypted` read. Running on the leading edge keeps the first event of
 * a burst instant, and the trailing run guarantees the LAST event still gets
 * its read — which matters, because the last event of a session is usually the
 * one that finalises the row.
 *
 * Kept out of the component so the timing can be tested against a hand-cranked
 * clock rather than a real one.
 */
export function createCoalescedTrigger({
  intervalMs,
  run,
  now = () => Date.now(),
  setTimer = (fn, ms) => window.setTimeout(fn, ms),
  clearTimer = (handle) => window.clearTimeout(handle),
}: CoalescedTriggerOptions): CoalescedTrigger {
  let lastRunAt = Number.NEGATIVE_INFINITY;
  let queued: number | null = null;
  let cancelled = false;

  const fire = () => {
    lastRunAt = now();
    run();
  };

  return {
    request() {
      if (cancelled || queued !== null) return;
      const wait = lastRunAt + intervalMs - now();
      if (wait <= 0) {
        fire();
        return;
      }
      queued = setTimer(() => {
        queued = null;
        if (cancelled) return;
        fire();
      }, wait);
    },
    cancel() {
      cancelled = true;
      if (queued !== null) {
        clearTimer(queued);
        queued = null;
      }
    },
  };
}
